import { useState, useEffect } from 'react';
import api from '../utils/api';
import { formatCurrency } from '../utils/format';
import toast from 'react-hot-toast';

const STATUS_COLOR = { Pending: '#F57F17', Approved: '#1B5E20', Denied: '#c62828' };

export default function ChangeOrders() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('Pending');
  const [selected, setSelected] = useState(null);
  const [approveForm, setApproveForm] = useState({ status: 'Approved', admin_notes: '', amount: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadRequests(); }, []);

  async function loadRequests() {
    try {
      const res = await api.get('/change-orders');
      setRequests(Array.isArray(res.data) ? res.data : []);
    } catch { toast.error('Failed to load requests'); }
    finally { setLoading(false); }
  }

  async function handleDecision() {
    if (!selected) return;
    if (approveForm.status === 'Approved' && !approveForm.amount) return toast.error('Enter the approved amount');
    setSaving(true);
    try {
      await api.put(`/change-orders/${selected.id}`, {
        status: approveForm.status,
        admin_notes: approveForm.admin_notes,
        amount: approveForm.amount
      });
      toast.success(approveForm.status === 'Approved' ? 'Change order approved!' : 'Change order denied');
      setSelected(null);
      loadRequests();
    } catch { toast.error('Failed to update'); }
    finally { setSaving(false); }
  }

  const filtered = requests.filter(r => filter === 'All' ? true : r.status === filter);
  const pendingCount = requests.filter(r => r.status === 'Pending').length;

  return (
    <>
      <div className="page-header">
        <h2>Change Order Requests</h2>
        {pendingCount > 0 && (
          <div style={{ background: '#F57F17', color: '#fff', borderRadius: 20, padding: '4px 12px', fontSize: 13, fontWeight: 700 }}>
            {pendingCount} Pending
          </div>
        )}
      </div>

      <div className="page-body">
        {/* Filter */}
        <div className="filter-bar" style={{ marginBottom: 20 }}>
          {['Pending', 'Approved', 'Denied', 'All'].map(s => (
            <button key={s} className={`filter-btn${filter === s ? ' active' : ''}`} onClick={() => setFilter(s)}>{s}</button>
          ))}
        </div>

        {loading ? (
          <div className="loader"><div className="spinner" /></div>
        ) : filtered.length === 0 ? (
          <div className="empty-state"><p>No {filter.toLowerCase()} change order requests</p></div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {filtered.map(r => (
              <div key={r.id} style={{ background: '#fff', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', padding: '16px 18px', borderLeft: '4px solid ' + (STATUS_COLOR[r.status] || '#ddd') }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 6 }}>
                      <span style={{ fontWeight: 700, fontSize: 14, color: '#0D3B2E' }}>{r.po_number}</span>
                      <span style={{ background: STATUS_COLOR[r.status]+'22', color: STATUS_COLOR[r.status], border: '1px solid '+STATUS_COLOR[r.status]+'44', padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600 }}>{r.status}</span>
                    </div>
                    <div style={{ fontSize: 13, color: '#333', marginBottom: 6 }}><strong>Contractor:</strong> {r.contractor_name}</div>
                    <div style={{ fontSize: 13, color: '#555', marginBottom: 6 }}>{r.description}</div>
                    {r.amount && <div style={{ fontSize: 13, fontWeight: 600, color: '#F57F17' }}>Requested amount: {formatCurrency(r.amount)}</div>}
                    {r.admin_notes && <div style={{ fontSize: 12, color: '#555', marginTop: 6, fontStyle: 'italic' }}>Admin notes: {r.admin_notes}</div>}
                    {r.photos && r.photos.length > 0 && (
                      <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                        {r.photos.map(photo => (
                          <a key={photo} href={`/api/change-orders/photo/${photo}`} target="_blank" rel="noreferrer">
                            <img src={`/api/change-orders/photo/${photo}`} alt="change order" style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 6, border: '1px solid #ddd' }} />
                          </a>
                        ))}
                      </div>
                    )}
                    <div style={{ fontSize: 11, color: '#aaa', marginTop: 8 }}>Submitted: {r.created_at}</div>
                  </div>

                  {r.status === 'Pending' && (
                    <button className="btn btn-primary btn-sm" onClick={() => { setSelected(r); setApproveForm({ status: 'Approved', admin_notes: '', amount: r.amount || '' }); }}>
                      Review
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Review modal */}
      {selected && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 10, padding: 28, width: 460, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <h3 style={{ margin: '0 0 16px', color: '#0D3B2E' }}>Review Change Order — {selected.po_number}</h3>
            <div style={{ background: '#f9f9f9', borderRadius: 8, padding: '12px 14px', marginBottom: 16, fontSize: 13 }}>
              <div style={{ marginBottom: 4 }}><strong>Contractor:</strong> {selected.contractor_name}</div>
              <div style={{ marginBottom: 4 }}><strong>Description:</strong> {selected.description}</div>
              {selected.amount && <div><strong>Requested:</strong> {formatCurrency(selected.amount)}</div>}
            </div>

            <div className="form-group" style={{ marginBottom: 14 }}>
              <label className="form-label">Decision</label>
              <select className="form-select" value={approveForm.status} onChange={e => setApproveForm(f => ({ ...f, status: e.target.value }))}>
                <option value="Approved">Approve</option>
                <option value="Denied">Deny</option>
              </select>
            </div>

            {approveForm.status === 'Approved' && (
              <div className="form-group" style={{ marginBottom: 14 }}>
                <label className="form-label">Approved Change Order Amount ($)</label>
                <input className="form-input" type="number" placeholder="0" value={approveForm.amount}
                  onChange={e => setApproveForm(f => ({ ...f, amount: e.target.value }))} />
                <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>This will update the job's change order amount field</div>
              </div>
            )}

            <div className="form-group" style={{ marginBottom: 20 }}>
              <label className="form-label">Admin Notes (optional)</label>
              <textarea className="form-textarea" rows={3} placeholder="Add any notes for the contractor..."
                value={approveForm.admin_notes} onChange={e => setApproveForm(f => ({ ...f, admin_notes: e.target.value }))} />
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={() => setSelected(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleDecision} disabled={saving}>
                {saving ? 'Saving…' : approveForm.status === 'Approved' ? 'Approve' : 'Deny'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
